import React, { useEffect, useState } from "react";

// import { useApolloClient, useLazyQuery } from "@apollo/client";
import { useLazyQuery } from "@apollo/client";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import loadable from "@loadable/component";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { message } from "@/common/Message";
import TableView from "@/common/TableView/TableView";
// import { EXPORT_CUSTOMERS_TO_CSV_QUERY } from "@/graphql/queries/exportCustomersToCsv";
import Tag from "@/common/Tag";
import { ORGANISATIONS_QUERY } from "@/graphql/queries/getOrganisations";
import useSearchParams from "@/hooks/useSearchParams";
import constants from "@/utils/constants";
// import downloadCsv from "@/utils/downloadCsv";
import getSortParam from "@/utils/getSortParam";
import { Sorter } from "@/utils/sorter";

const Button = loadable(() => import("@/common/Button"));
const Input = loadable(() => import("@/common/Input"));
const Layout = loadable(() => import("@/common/Layout"));
const OrganisationFormModal = loadable(() => import("./OrganisationFormModal"));

export default function Organisations() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // const client = useApolloClient();
  const [searchTerm, setSearchTerm] = useState("");
  const { search, updateSearchParams } = useSearchParams();
  const [sorter, setSorter] = useState();

  const [
    getOrganisations,
    {
      loading: organisationsLoading,
      data: organisationsData,
      error: organisationsError,
      refetch: updateOrganisationsTable,
    },
  ] = useLazyQuery(ORGANISATIONS_QUERY, {
    context: { graph: "restaurants" },
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      if (data.organisations) {
        if (
          data.organisations.page >
          data.organisations.totalCount / data.organisations.perPage
        ) {
          // If over paged after searching, reset to page 1
          updateSearchParams({
            page: 1,
          });
        }
      }
    },
  });

  useEffect(() => {
    if (organisationsError?.message) {
      message.error(organisationsError?.message);
    }
  }, [organisationsError]);

  useEffect(() => {
    getOrganisations({
      variables: {
        filter: {
          name: search.name,
          page: search.page ? parseInt(search.page) : 1,
          perPage: search.pageSize
            ? parseInt(search.pageSize)
            : constants.DEFAULT_PER_PAGE,
        },
        ...getSortParam(sorter),
      },
    });
  }, [
    search.name,
    search.page,
    search.pageSize,
    getOrganisations,
    search.tab,
    sorter,
  ]);

  const getColumns = () => {
    return [
      {
        title: t("organisations.tableColumns.id"),
        dataIndex: "id",
        sorter: {
          compare: Sorter.DEFAULT,
        },
      },
      {
        title: t("organisations.tableColumns.name"),
        dataIndex: "name",
        sorter: {
          compare: Sorter.DEFAULT,
        },
      },
      {
        title: t("organisations.tableColumns.address"),
        sorter: {
          compare: Sorter.DEFAULT,
        },
        render: (_, record) => {
          return (
            <div>
              <div>{record.addressLine1}</div>
              <div>{record.addressLine2}</div>
              <div>{record.postalCode}</div>
            </div>
          );
        },
      },
      {
        title: t("organisations.tableColumns.sector"),
        dataIndex: "sector",
        sorter: {
          compare: Sorter.DEFAULT,
        },
      },
      {
        title: t("organisations.tableColumns.tagList"),
        dataIndex: "tagList",
        render: (tagList) => {
          return (
            <div className="mt-2">
              {tagList?.length > 0 &&
                tagList.map((tag) => (
                  <span key={tag} className="inline-block mr-1">
                    <Tag className="text-xs" rounded>
                      {tag}
                    </Tag>
                  </span>
                ))}
            </div>
          );
        },
      },
      {
        title: t("organisations.tableColumns.domainName"),
        dataIndex: "domainName",
      },
      {
        title: t("organisations.tableColumns.notes"),
        dataIndex: "notes",
      },
      {
        title: t("organisations.tableColumns.paymentTermsInDays"),
        dataIndex: "paymentTermsInDays",
        render: (paymentTermsInDays) => {
          return paymentTermsInDays || "N/A";
        },
      },
      {
        title: t("common.tableColumns.edit"),
        key: "edit",
        render: (_, record) => {
          return (
            <Link
              to={`/organisations/${record.id}/edit${location.search}`}
              className="text-indigo-600 hover:text-indigo-700"
            >
              {t("common.tableValues.edit")}
            </Link>
          );
        },
      },
    ];
  };

  return (
    <Layout
      className="organisations"
      pageTitle={t("organisations.header")}
      fullScreen
    >
      <div className="flex flex-col h-full p-4">
        <div className="flex gap-4 mb-4 text-right">
          <Input
            className="flex w-full mb-4"
            inputContainerClassName="w-full h-12"
            inputClassName={
              searchTerm ? "bg-indigo-100 border-indigo-300" : null
            }
            type="search"
            placeholder={t("organisations.searchPlaceholder")}
            leftIcon={
              searchTerm.length > 0 ? null : (
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
              )
            }
            style={{ width: "100%" }}
            value={searchTerm}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                updateSearchParams({ name: searchTerm });
              }
            }}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <Button
            className="flex"
            type="primary"
            onClick={() => updateSearchParams({ name: searchTerm })}
          >
            {<MagnifyingGlassIcon className="w-5 h-5 text-white" />}
          </Button>
          <Button
            className="min-w-[140px]"
            type="primary"
            onClick={() => navigate(`/organisations/new${location.search}`)}
          >
            {t("organisations.actions.add")}
          </Button>
        </div>

        <TableView
          rowKey="id"
          loading={organisationsLoading}
          columns={getColumns(t)}
          dataSource={organisationsData?.allOrganizations.organizations || []}
          pagination={{
            current: organisationsData?.allOrganizations.page || 1,
            pageSize:
              organisationsData?.allOrganizations.perPage ||
              constants.DEFAULT_PER_PAGE,
            total: organisationsData?.allOrganizations.totalCount || 0,
          }}
          onChange={(_pagination, _filters, sorter) => {
            setSorter(sorter);
          }}
        />

        <OrganisationFormModal
          organisations={
            organisationsData?.allOrganizations.organizations || []
          }
          updateOrganisationsTable={updateOrganisationsTable}
        />
      </div>
    </Layout>
  );
}
